import axios from 'axios'
import * as t from 'io-ts'

import { Org } from '@espoon-voltti/nuora-domain/applicants'

import {
  ProjectGrantDraftApp,
  GeneralGrantDraftApp,
  GrantApp,
  SentApp,
  Signer,
  SigningApp,
  ValidDraftApp,
  ValidGeneralGrantDraftApp
} from '../../domain'
import { GrantPeriodType } from '@espoon-voltti/nuora-domain/grants'

import {
  CreateOrUpdateDraftDto,
  GrantAppMapper,
  DraftAppDto,
  GeneralGrantDraftAppDto,
  DraftAppMapper,
  SendForSigningDto,
  AppDto
} from '../dtos'
import { grantsApiUrl, extractResponseData } from './utils'
import { orgApi } from './orgs'

const fetchOrgIfApplicable = async (
  x: AppDto
): Promise<CreateOrUpdateDraftDto | AppDto> => {
  if (x.state === 'DRAFT') {
    x.applicant.org = await orgApi.getByIdRaw(x.applicant.org.id)
  }
  return x
}

const appsApiUrl = `${grantsApiUrl}/grant-applications`

export interface AppsApi {
  createDraft(
    orgId: string,
    grantPeriodId: string,
    grantPeriodType: GrantPeriodType
  ): Promise<ProjectGrantDraftApp | GeneralGrantDraftApp>
  deleteById(appId: string): Promise<unknown>
  getById(appId: string): Promise<GrantApp>
  getByOrg(orgId: string): Promise<GrantApp[]>
  getSignersByAppId(appId: string): Promise<Signer[]>
  saveDraft(
    app: ProjectGrantDraftApp
  ): Promise<ProjectGrantDraftApp | GeneralGrantDraftApp>
  sendForSigning(
    org: Org,
    app: ValidDraftApp | ValidGeneralGrantDraftApp
  ): Promise<unknown>
  sign(appId: string): Promise<SigningApp | SentApp>
}
class AppsApiClient implements AppsApi {
  public async createDraft(
    orgId: string,
    grantPeriodId: string,
    grantPeriodType: GrantPeriodType
  ): Promise<ProjectGrantDraftApp | GeneralGrantDraftApp> {
    switch (grantPeriodType) {
      case 'YOUTH_PROJECT_GRANT':
        return axios
          .post(
            appsApiUrl,
            CreateOrUpdateDraftDto.encode(
              DraftAppMapper.toInitialRequestDto(
                grantPeriodId,
                orgId,
                grantPeriodType
              )
            )
          )
          .then(extractResponseData)
          .then(fetchOrgIfApplicable)
          .then((data) => {
            const validAppDto = DraftAppDto.decodeOrThrow(data)
            return DraftAppMapper.fromDto(validAppDto)
          })
      case 'YOUTH_GENERAL_GRANT':
        const url: string = `${appsApiUrl}?general`
        return axios
          .post(
            url,
            CreateOrUpdateDraftDto.encode(
              DraftAppMapper.toInitialRequestDto(
                grantPeriodId,
                orgId,
                grantPeriodType
              )
            )
          )
          .then(extractResponseData)
          .then(fetchOrgIfApplicable)
          .then((data) => {
            const validAppDto = GeneralGrantDraftAppDto.decodeOrThrow(data)
            return DraftAppMapper.fromDto(validAppDto)
          })
    }
  }

  public deleteById(appId: string) {
    return axios.delete(`${appsApiUrl}/${appId}`)
  }

  public async getById(appId: string): Promise<GrantApp> {
    return axios
      .get(`${appsApiUrl}/${appId}`)
      .then(extractResponseData)
      .then(fetchOrgIfApplicable)
      .then(async (data) =>
        GrantAppMapper.fromRaw(data, await this.getSignersByAppId(appId))
      )
  }

  public async getByOrg(orgId: string): Promise<GrantApp[]> {
    const params = { orgId }

    return axios
      .get(appsApiUrl, { params })
      .then(extractResponseData)
      .then((xs) => Promise.all(xs.map(fetchOrgIfApplicable)))
      .then((data) =>
        Promise.all(
          data.map(async (x: any) =>
            // NOTE very inefficient, should be fixed in backend
            GrantAppMapper.fromRaw(x, await this.getSignersByAppId(x.id))
          )
        )
      )
  }

  public async getSignersByAppId(appId: string): Promise<Signer[]> {
    return axios
      .get(`${appsApiUrl}/${appId}/signers`)
      .then(extractResponseData)
      .then((data) => t.array(Signer).decodeOrThrow(data))
  }

  public async saveDraft(
    app: ProjectGrantDraftApp
  ): Promise<ProjectGrantDraftApp | GeneralGrantDraftApp> {
    const url =
      app.form.type === 'PROJECT_GRANT'
        ? `${appsApiUrl}/${app.id}`
        : `${appsApiUrl}/${app.id}?general`
    return axios
      .put(`${url}`, CreateOrUpdateDraftDto.encode(DraftAppMapper.toDto(app)))
      .then(extractResponseData)
      .then(fetchOrgIfApplicable)
      .then((data) => {
        const validAppDto = DraftAppDto.decodeOrThrow(data)
        return DraftAppMapper.fromDto(validAppDto)
      })
  }

  public sendForSigning(
    org: Org,
    app: ValidDraftApp | ValidGeneralGrantDraftApp
  ): Promise<unknown> {
    const url =
      app.form.type === 'PROJECT_GRANT'
        ? `${appsApiUrl}/${app.id}/send-for-signing`
        : `${appsApiUrl}/${app.id}/send-for-signing?general`
    return axios.put(
      url,
      SendForSigningDto.encode(DraftAppMapper.toSendForSigningDto(org, app))
    )
  }

  public async sign(appId: string): Promise<SigningApp | SentApp> {
    return axios
      .post(`${appsApiUrl}/${appId}/signers`)
      .then(() => axios.get(`${appsApiUrl}/${appId}`))
      .then(extractResponseData)
      .then(async (data) =>
        GrantAppMapper.signResponseFromRaw(
          data,
          await this.getSignersByAppId(appId)
        )
      )
  }
}
export const appsApi: AppsApi = new AppsApiClient()
